import React, { useEffect, useMemo, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Copy, Plus, Eye, X, Check } from "lucide-react";
import { format, parseISO } from "date-fns";

const TEST_KINDS = [
  { value: "star_reading", label: "STAR Reading (Primary G1–G6)" },
  { value: "map", label: "MAP (Reading + Math + Language)" },
];
const SUBJECTS = [
  { value: "reading", label: "Reading" },
  { value: "math", label: "Math" },
  { value: "language", label: "Language Usage" },
];
const STAR_BANDS = ["G1", "G2", "G3", "G4", "G5", "G6"];
const MAP_BANDS = ["G1-3", "G4-6", "G7-9", "G10-12"];
const ALL_GRADES = ["G1","G2","G3","G4","G5","G6","G7","G8","G9","G10","G11","G12"];
const SKILLS_BY_SUBJECT = {
  reading: ["vocab", "literal", "inferential", "main_idea", "author_purpose", "text_structure"],
  math: ["number_ops", "fractions_decimals", "algebra", "geometry", "data_stats"],
  language: ["grammar", "punctuation", "usage", "vocabulary_in_context", "writing_revision"],
};

const EMPTY_Q = { test_kind: "star_reading", subject: "reading", grade_band: "G3", difficulty: 3, skill: "vocab", passage: "", prompt: "", options: ["", "", "", ""], answer_index: 0 };

function QuestionForm({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setOption = (i, v) => { const nx = [...value.options]; nx[i] = v; onChange({ ...value, options: nx }); };
  const bandOptions = value.test_kind === "star_reading" ? STAR_BANDS : MAP_BANDS;
  const skills = SKILLS_BY_SUBJECT[value.subject] || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
      <div className="md:col-span-3"><Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Test</Label>
        <Select value={value.test_kind} onValueChange={(v)=>set("test_kind", v)}>
          <SelectTrigger data-testid="mockq-test_kind" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{TEST_KINDS.map(t=><SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-3"><Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Subject</Label>
        <Select value={value.subject} onValueChange={(v)=>set("subject", v)} disabled={value.test_kind === "star_reading"}>
          <SelectTrigger data-testid="mockq-subject" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{SUBJECTS.map(s=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2"><Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Grade band</Label>
        <Select value={value.grade_band} onValueChange={(v)=>set("grade_band", v)}>
          <SelectTrigger data-testid="mockq-band" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{bandOptions.map(b=><SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2"><Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Difficulty (1-5)</Label>
        <Select value={String(value.difficulty)} onValueChange={(v)=>set("difficulty", parseInt(v, 10))}>
          <SelectTrigger data-testid="mockq-difficulty" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{[1,2,3,4,5].map(d=><SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2"><Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Skill</Label>
        <Select value={value.skill} onValueChange={(v)=>set("skill", v)}>
          <SelectTrigger data-testid="mockq-skill" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{skills.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-12"><Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Passage (optional)</Label>
        <Textarea rows={2} value={value.passage || ""} onChange={(e)=>set("passage", e.target.value)} data-testid="mockq-passage" className="mt-2" />
      </div>
      <div className="md:col-span-12"><Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Prompt</Label>
        <Textarea rows={2} value={value.prompt} onChange={(e)=>set("prompt", e.target.value)} data-testid="mockq-prompt" className="mt-2" />
      </div>
      {value.options.map((opt, i) => (
        <div key={i} className="md:col-span-6">
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Option {String.fromCharCode(65+i)} {i === value.answer_index && <span className="text-[hsl(var(--primary))]">· correct</span>}</Label>
          <Input value={opt} onChange={(e)=>setOption(i, e.target.value)} data-testid={`mockq-opt-${i}`} className="mt-2" />
        </div>
      ))}
      <div className="md:col-span-3"><Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Correct option</Label>
        <Select value={String(value.answer_index)} onValueChange={(v)=>set("answer_index", parseInt(v, 10))}>
          <SelectTrigger data-testid="mockq-answer" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{value.options.map((_, i)=><SelectItem key={i} value={String(i)}>Option {String.fromCharCode(65+i)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

function QuestionsTab() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY_Q);
  const [filter, setFilter] = useState({ test_kind: "all", subject: "all", grade_band: "all" });

  async function load() {
    try { const { data } = await api.get("/admin/mock/questions"); setItems(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  const filtered = useMemo(() => items.filter(q =>
    (filter.test_kind === "all" || q.test_kind === filter.test_kind)
    && (filter.subject === "all" || q.subject === filter.subject)
    && (filter.grade_band === "all" || q.grade_band === filter.grade_band)), [items, filter]);

  async function create() {
    if (!draft.prompt.trim() || draft.options.some(o => !o.trim())) { toast.error("Fill prompt and all options"); return; }
    try {
      await api.post("/admin/mock/questions", draft);
      toast.success("Question created");
      setCreating(false); setDraft(EMPTY_Q); load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }
  async function del(id) {
    if (!window.confirm("Delete question?")) return;
    try { await api.delete(`/admin/mock/questions/${id}`); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Mock test bank · {filtered.length} / {items.length}</div>
        <div className="flex items-end gap-2 flex-wrap">
          <Select value={filter.test_kind} onValueChange={(v)=>setFilter({...filter, test_kind: v})}>
            <SelectTrigger data-testid="mockq-filter-test" className="h-9 w-44 text-xs"><SelectValue placeholder="Test" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All tests</SelectItem>{TEST_KINDS.map(t=><SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filter.subject} onValueChange={(v)=>setFilter({...filter, subject: v})}>
            <SelectTrigger data-testid="mockq-filter-subject" className="h-9 w-36 text-xs"><SelectValue placeholder="Subject" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All subjects</SelectItem>{SUBJECTS.map(s=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filter.grade_band} onValueChange={(v)=>setFilter({...filter, grade_band: v})}>
            <SelectTrigger data-testid="mockq-filter-band" className="h-9 w-32 text-xs"><SelectValue placeholder="Grade" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All grades</SelectItem>{[...STAR_BANDS, ...MAP_BANDS].map(b=><SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={()=>setCreating(v=>!v)} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="mockq-new-toggle">
            {creating ? <><X className="w-3.5 h-3.5 mr-1.5" />Cancel</> : <><Plus className="w-3.5 h-3.5 mr-1.5" />New question</>}
          </Button>
        </div>
      </div>
      {creating && (
        <div className="bg-white border border-[hsl(var(--border))] rounded-2xl p-5 space-y-3" data-testid="mockq-create-form">
          <QuestionForm value={draft} onChange={setDraft} />
          <div className="flex justify-end"><Button onClick={create} className="rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:bg-[hsl(var(--foreground))]/90" data-testid="mockq-create-submit"><Check className="w-4 h-4 mr-1.5" />Create</Button></div>
        </div>
      )}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))]/50">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
              <th className="py-2 px-3">Test</th><th className="py-2 px-3">Subject</th><th className="py-2 px-3">Band</th>
              <th className="py-2 px-3">Diff</th><th className="py-2 px-3">Skill</th><th className="py-2 px-3">Prompt</th><th className="py-2 px-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(q => (
              <tr key={q.id} data-testid={`mockq-row-${q.id}`} className="border-t border-[hsl(var(--border))]/60">
                <td className="py-2 px-3 text-xs">{q.test_kind === "star_reading" ? "STAR" : "MAP"}</td>
                <td className="py-2 px-3 text-xs">{q.subject}</td>
                <td className="py-2 px-3 text-xs">{q.grade_band}</td>
                <td className="py-2 px-3 text-xs">{q.difficulty}</td>
                <td className="py-2 px-3 text-xs">{q.skill}</td>
                <td className="py-2 px-3 text-xs max-w-md truncate">{q.prompt}</td>
                <td className="py-2 px-3"><Button size="sm" variant="ghost" onClick={()=>del(q.id)} data-testid={`mockq-del-${q.id}`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-[hsl(var(--muted-foreground))] text-xs">No questions match the filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvitesTab() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ test_kind: "star_reading", claimed_grade: "G3", for_student_name: "", for_email: "", notes: "" });

  async function load() {
    try { const { data } = await api.get("/admin/mock/invites"); setItems(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function create() {
    if (draft.test_kind === "star_reading" && !STAR_BANDS.includes(draft.claimed_grade)) {
      toast.error("STAR Reading is Primary (G1–G6) only"); return;
    }
    try {
      const payload = { ...draft };
      if (!payload.for_email) delete payload.for_email;
      if (!payload.for_student_name) delete payload.for_student_name;
      if (!payload.notes) delete payload.notes;
      await api.post("/admin/mock/invites", payload);
      toast.success("Test code generated");
      setCreating(false); setDraft({ test_kind: "star_reading", claimed_grade: "G3", for_student_name: "", for_email: "", notes: "" });
      load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }
  async function del(id) {
    if (!window.confirm("Delete invite?")) return;
    try { await api.delete(`/admin/mock/invites/${id}`); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  function copyCode(code) {
    navigator.clipboard?.writeText(code);
    toast.success(`Code "${code}" copied`);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Invite codes · {items.length}</div>
        <Button size="sm" onClick={()=>setCreating(v=>!v)} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="mock-invite-new-toggle">
          {creating ? <><X className="w-3.5 h-3.5 mr-1.5" />Cancel</> : <><Plus className="w-3.5 h-3.5 mr-1.5" />New code</>}
        </Button>
      </div>
      {creating && (
        <div className="bg-white border border-[hsl(var(--border))] rounded-2xl p-5 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="mock-invite-create-form">
          <div className="md:col-span-3"><Label className="text-xs">Test kind</Label>
            <Select value={draft.test_kind} onValueChange={(v)=>setDraft({...draft, test_kind: v})}>
              <SelectTrigger data-testid="mock-invite-test" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{TEST_KINDS.map(t=><SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label className="text-xs">Student grade</Label>
            <Select value={draft.claimed_grade} onValueChange={(v)=>setDraft({...draft, claimed_grade: v})}>
              <SelectTrigger data-testid="mock-invite-grade" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{ALL_GRADES.map(g=><SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3"><Label className="text-xs">Student name</Label><Input value={draft.for_student_name} onChange={(e)=>setDraft({...draft, for_student_name: e.target.value})} data-testid="mock-invite-name" className="mt-1" /></div>
          <div className="md:col-span-4"><Label className="text-xs">Email (lock to one student)</Label><Input type="email" value={draft.for_email} onChange={(e)=>setDraft({...draft, for_email: e.target.value})} data-testid="mock-invite-email" className="mt-1" /></div>
          <div className="md:col-span-12"><Label className="text-xs">Notes</Label><Textarea rows={2} value={draft.notes} onChange={(e)=>setDraft({...draft, notes: e.target.value})} data-testid="mock-invite-notes" className="mt-1" /></div>
          <div className="md:col-span-12 flex justify-end"><Button onClick={create} className="rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:bg-[hsl(var(--foreground))]/90" data-testid="mock-invite-submit"><Check className="w-4 h-4 mr-1.5" />Generate code</Button></div>
        </div>
      )}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))]/50">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
              <th className="py-2 px-3">Code</th><th className="py-2 px-3">Test</th><th className="py-2 px-3">Grade</th>
              <th className="py-2 px-3">Student</th><th className="py-2 px-3">Status</th><th className="py-2 px-3">Created</th><th className="py-2 px-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(inv => (
              <tr key={inv.id} data-testid={`mock-invite-${inv.id}`} className="border-t border-[hsl(var(--border))]/60">
                <td className="py-2 px-3 font-mono text-xs">{inv.code}</td>
                <td className="py-2 px-3 text-xs">{inv.test_kind === "star_reading" ? "STAR" : "MAP"}</td>
                <td className="py-2 px-3 text-xs">{inv.claimed_grade}</td>
                <td className="py-2 px-3 text-xs">{inv.for_student_name || inv.for_email || "—"}</td>
                <td className="py-2 px-3 text-xs">{inv.status}</td>
                <td className="py-2 px-3 text-xs">{inv.created_at ? format(parseISO(inv.created_at), "MMM d, HH:mm") : ""}</td>
                <td className="py-2 px-3 flex gap-1">
                  <Button size="sm" variant="ghost" onClick={()=>copyCode(inv.code)} data-testid={`mock-invite-${inv.id}-copy`}><Copy className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>del(inv.id)} data-testid={`mock-invite-${inv.id}-del`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-[hsl(var(--muted-foreground))] text-xs">No invites yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AttemptsTab() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  async function load() {
    try { const { data } = await api.get("/admin/mock/attempts"); setItems(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function openDetail(id) {
    try { const { data } = await api.get(`/admin/mock/attempts/${id}`); setSelected(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  return (
    <div className="space-y-4">
      <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Attempts · {items.length}</div>
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))]/50">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
              <th className="py-2 px-3">Student</th><th className="py-2 px-3">Test</th><th className="py-2 px-3">Grade</th>
              <th className="py-2 px-3">Status</th><th className="py-2 px-3">Result</th><th className="py-2 px-3">Created</th><th className="py-2 px-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(att => (
              <tr key={att.id} data-testid={`mock-attempt-${att.id}`} className="border-t border-[hsl(var(--border))]/60">
                <td className="py-2 px-3 text-xs">{att.student_name || att.student_email}</td>
                <td className="py-2 px-3 text-xs">{att.test_kind === "star_reading" ? "STAR" : "MAP"}</td>
                <td className="py-2 px-3 text-xs">{att.claimed_grade}</td>
                <td className="py-2 px-3 text-xs">{att.status}</td>
                <td className="py-2 px-3 text-xs">
                  {att.result?.scaled_score ? `${att.result.scaled_score} · GE ${att.result.grade_equivalent}` : ""}
                  {att.result?.rit_by_subject ? Object.entries(att.result.rit_by_subject).map(([s,v])=>`${s}:${v.rit}`).join(" · ") : ""}
                </td>
                <td className="py-2 px-3 text-xs">{att.created_at ? format(parseISO(att.created_at), "MMM d, HH:mm") : ""}</td>
                <td className="py-2 px-3"><Button size="sm" variant="ghost" onClick={()=>openDetail(att.id)} data-testid={`mock-attempt-${att.id}-view`}><Eye className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-[hsl(var(--muted-foreground))] text-xs">No attempts yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={()=>setSelected(null)} data-testid="mock-attempt-detail">
          <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--primary))]">{selected.test_kind === "star_reading" ? "STAR Reading" : "MAP"} · {selected.claimed_grade}</p>
                <h3 className="font-display text-2xl mt-1">{selected.student_name || selected.student_email}</h3>
              </div>
              <Button size="sm" variant="ghost" onClick={()=>setSelected(null)}><X className="w-4 h-4" /></Button>
            </div>
            {selected.result && (
              <div className="rounded-xl bg-[hsl(var(--muted))]/50 p-4 text-sm">
                {selected.result.scaled_score && <p><strong>Scaled:</strong> {selected.result.scaled_score} · GE {selected.result.grade_equivalent} · {selected.result.instructional_level}</p>}
                {selected.result.rit_by_subject && Object.entries(selected.result.rit_by_subject).map(([s,v])=>(
                  <p key={s}><strong>{s}:</strong> RIT {v.rit}</p>
                ))}
                {selected.result.strengths?.length > 0 && <p className="mt-2"><strong>Strengths:</strong> {selected.result.strengths.join(", ")}</p>}
                {selected.result.weaknesses?.length > 0 && <p><strong>To strengthen:</strong> {selected.result.weaknesses.join(", ")}</p>}
              </div>
            )}
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Answers · {selected.answers_detailed?.length || 0}</p>
              <ol className="mt-2 space-y-3 text-sm">
                {(selected.answers_detailed || []).map(a => (
                  <li key={a.n} className="rounded-lg border border-[hsl(var(--border))] p-3">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
                      <span>#{a.n}</span><span>· {a.subject} · {a.skill} · D{a.difficulty_at_ask}</span>
                      <span className={a.correct ? "text-emerald-600" : "text-red-600"}>· {a.correct ? "Correct" : "Wrong"}</span>
                    </div>
                    {a.passage && <p className="mt-1 italic text-xs text-[hsl(var(--muted-foreground))]">{a.passage}</p>}
                    <p className="font-medium mt-1">{a.prompt}</p>
                    <ul className="mt-1 ml-4 list-disc text-xs">
                      {a.options.map((o, i) => (
                        <li key={i} className={i === a.correct_answer_index ? "text-emerald-700" : (i === a.student_answer_index ? "text-red-700" : "")}>
                          {o}{i === a.correct_answer_index ? " ✓" : ""}{i === a.student_answer_index && i !== a.correct_answer_index ? " (chosen)" : ""}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MockTestsPanel() {
  return (
    <div data-testid="admin-mock-panel" className="space-y-4">
      <Tabs defaultValue="invites" className="w-full">
        <TabsList className="bg-[hsl(var(--muted))] p-1 rounded-full h-auto">
          <TabsTrigger value="invites" data-testid="mock-tab-invites" className="rounded-full data-[state=active]:bg-white px-5">Invite codes</TabsTrigger>
          <TabsTrigger value="attempts" data-testid="mock-tab-attempts" className="rounded-full data-[state=active]:bg-white px-5">Attempts &amp; results</TabsTrigger>
          <TabsTrigger value="questions" data-testid="mock-tab-questions" className="rounded-full data-[state=active]:bg-white px-5">Question bank</TabsTrigger>
        </TabsList>
        <TabsContent value="invites" className="mt-6"><InvitesTab /></TabsContent>
        <TabsContent value="attempts" className="mt-6"><AttemptsTab /></TabsContent>
        <TabsContent value="questions" className="mt-6"><QuestionsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
